import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { apiEvents } from './api.ts';
import { anyDirty, flushAll } from './dirty.ts';

/**
 * Service worker registration, the update prompt and the install prompt (roadmap 3.1, 3.9).
 *
 * A new build is installed in the background and then WAITS: nothing reloads by itself, because the
 * person may be halfway through a form. The banner says a new version is ready; accepting saves every
 * open form's draft first (flushAll), then activates the new worker and reloads, and the form offers its
 * draft back. A 426 CLIENT_OUTDATED from the API raises the same banner.
 */
type InstallPromptEvent = Event & { prompt(): Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> };

type Pwa = {
  updateReady: boolean;
  outdated: boolean;
  /** Saves drafts, then swaps to the new version (reloads the page). */
  applyUpdate(): Promise<void>;
  canInstall: boolean;
  install(): Promise<void>;
  installed: boolean;
};

const PwaContext = createContext<Pwa | null>(null);

// The install prompt can fire before React mounts; keep it from the first moment.
let deferredPrompt: InstallPromptEvent | null = null;
const installTarget = new EventTarget();
globalThis.addEventListener?.('beforeinstallprompt', (e) => {
  e.preventDefault();   // our own button, never the browser's unprompted mini-infobar
  deferredPrompt = e as InstallPromptEvent;
  installTarget.dispatchEvent(new Event('change'));
});
globalThis.addEventListener?.('appinstalled', () => {
  deferredPrompt = null;
  installTarget.dispatchEvent(new Event('installed'));
});

const UPDATE_CHECK_MS = 30 * 60_000;

export function PwaProvider({ children }: { children: ReactNode }) {
  const { needRefresh: [needRefresh], updateServiceWorker } = useRegisterSW({
    onRegisteredSW(_url, reg) {
      if (!reg) return;
      setInterval(() => void reg.update().catch(() => {}), UPDATE_CHECK_MS);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void reg.update().catch(() => {});
      });
    },
  });
  const [outdated, setOutdated] = useState(false);
  const [canInstall, setCanInstall] = useState(deferredPrompt !== null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onOutdated = () => setOutdated(true);
    const onChange = () => setCanInstall(deferredPrompt !== null);
    const onInstalled = () => {
      setCanInstall(false);
      setInstalled(true);
    };
    apiEvents.addEventListener('outdated', onOutdated);
    installTarget.addEventListener('change', onChange);
    installTarget.addEventListener('installed', onInstalled);
    return () => {
      apiEvents.removeEventListener('outdated', onOutdated);
      installTarget.removeEventListener('change', onChange);
      installTarget.removeEventListener('installed', onInstalled);
    };
  }, []);

  const applyUpdate = useCallback(async () => {
    await flushAll();
    if (needRefresh) await updateServiceWorker(true);
    else globalThis.location.reload();
  }, [needRefresh, updateServiceWorker]);

  const install = useCallback(async () => {
    const p = deferredPrompt;
    if (!p) return;
    deferredPrompt = null;
    setCanInstall(false);
    await p.prompt();
    if ((await p.userChoice).outcome === 'accepted') setInstalled(true);
  }, []);

  const value = useMemo<Pwa>(() => ({ updateReady: needRefresh, outdated, applyUpdate, canInstall, install, installed }),
    [needRefresh, outdated, applyUpdate, canInstall, install, installed]);
  return <PwaContext.Provider value={value}>{children}</PwaContext.Provider>;
}

export function usePwa(): Pwa {
  const v = useContext(PwaContext);
  if (!v) throw new Error('usePwa outside PwaProvider');
  return v;
}

export { anyDirty };
